import React from 'react';
import { aufloesePikto, piktoPfad } from '../piktogramme';
import { SkizzeSvg } from '../skizzen/SkizzeSvg';
import { zeichneSchleuse } from '../skizzen/schleuse';
import type { NotfallStelle } from '../bestandUebernahme';
import type { NotrufZeile } from './aushangKatalog';
import type { SchleusenParameter, Schutzklasse } from '../types';

/**
 * Die Aushaenge des A+S-Plans — Papier fuer den Container und den Bauzaun.
 *
 * ═══ WARUM BROWSERDRUCK UND NICHT jsPDF ═══════════════════════════════════
 * Ein Aushang ist das Gegenteil eines Plans: grosse Flaeche, grosse Schrift,
 * wenige Woerter, Piktogramme. Dafuer ist HTML mit `@media print` das
 * geeignetere Werkzeug — der Umbruch ist unkritisch, weil jedes Blatt genau
 * eine Seite ist, und die ISO-7010-Zeichen kommen als Bild statt als
 * gerasterte Canvas-Kopie. Der Plan selbst bleibt bei jsPDF, weil er 40
 * Seiten mit Kopf-, Fusszeile und Briefpapier hat.
 *
 * ═══ WAS EIN AUSHANG NICHT IST ════════════════════════════════════════════
 * Er ist kein Auszug des Plans. Was hier steht, muss ohne den Plan
 * verstaendlich sein — wer davorsteht, hat das Dokument nicht in der Hand und
 * wird es auch nicht holen. Deshalb keine Verweise auf Kapitelnummern und
 * keine Abkuerzungen, die nur im Plan erklaert sind.
 */

// ═══════════════════════════════════════════════════════════════════════════
// RAHMEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Aushangblatt.
 *
 * `break-after-page` sorgt dafuer, dass beim Drucken mehrerer Blaetter jedes
 * auf einer eigenen Seite landet. Am Bildschirm steht das Blatt im
 * Seitenverhaeltnis, damit man vor dem Druck sieht, was nicht passt — eine
 * gedruckte Seite kann nicht wachsen (dieselbe Ueberlegung wie bei `A3Seite`
 * im Handbuch).
 */
export const Aushangblatt: React.FC<{
    titel: string;
    untertitel?: string;
    fuss?: string;
    hoch?: boolean;
    children: React.ReactNode;
}> = ({ titel, untertitel, fuss, hoch = true, children }) => (
    <div
        className="mx-auto mb-6 w-full max-w-[900px] bg-white border-2 border-slate-800 flex flex-col
                   print:mb-0 print:border-0 print:max-w-none print:break-after-page"
        style={{ aspectRatio: hoch ? '1 / 1.414' : '1.414 / 1', padding: '24px 28px' }}
    >
        <header className="border-b-4 border-slate-800 pb-2 mb-4">
            <h1 className="text-3xl font-black text-slate-900 leading-none">{titel}</h1>
            {untertitel && <p className="text-sm font-bold text-slate-600 mt-1">{untertitel}</p>}
        </header>

        <div className="flex-1 min-h-0 overflow-hidden">{children}</div>

        {fuss && (
            <footer className="border-t border-slate-300 pt-1.5 mt-3 text-[10px] text-slate-500">
                {fuss}
            </footer>
        )}
    </div>
);

/** Ein ISO-7010-Zeichen mit Text darunter. */
const Zeichen: React.FC<{ code: string; text?: string; gross?: boolean }> = ({ code, text, gross }) => {
    const auf = aufloesePikto(code);
    if (!auf) return null;
    const kante = gross ? 86 : 58;
    return (
        <figure className="flex flex-col items-center gap-1 shrink-0" style={{ width: kante + 26 }}>
            {auf.iso
                ? <img src={piktoPfad(auf.iso)} alt={auf.label} style={{ width: kante, height: kante }} />
                : <div
                    className="flex items-center justify-center border-2 border-slate-800 rounded-full font-black"
                    style={{ width: kante, height: kante }}
                >{auf.symbol}</div>}
            <figcaption className="text-[10px] font-bold text-center leading-tight text-slate-700">
                {text ?? auf.label}
            </figcaption>
        </figure>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// 1) VERHALTEN IM NOTFALL
// ═══════════════════════════════════════════════════════════════════════════

export const AushangNotfall: React.FC<{
    projektName: string;
    stoffe: string[];
    /** Aus `notrufZeilen()` — Reihenfolge und Herkunft stehen dort. */
    notrufnummern: NotrufZeile[];
    sammelplatz: string;
    /** Aus `notrufLuecken()`. Wird UNTEN auf dem Blatt genannt, nicht
     *  verschwiegen: ein Notfallblatt mit stillen Luecken sieht gepflegt aus
     *  und ist es nicht. */
    luecken?: string[];
}> = ({ projektName, stoffe, notrufnummern, sammelplatz, luecken = [] }) => (
    <Aushangblatt
        titel="Verhalten im Notfall"
        untertitel={projektName}
        fuss="Arbeiten in kontaminierten Bereichen · DGUV Regel 101-004"
    >
        <ol className="space-y-2.5 mb-5">
            {[
                ['Ruhe bewahren', 'Eigene Sicherheit zuerst: die persönliche Schutzausrüstung nicht voreilig ablegen.'],
                ['Retten, wenn es gefahrlos möglich ist', 'Verunfallte nur bergen, wenn die eigene Sicherung steht.'],
                ['Notruf 112', 'Wer? Wo? Was? Wie viele? UND: welche Gefahrstoffe.'],
                ['Ersthelfer alarmieren', 'Erste Hilfe leisten, Notfallausweis der verunfallten Person bereithalten.'],
                ['Koordinator und Bauleitung informieren', 'Nummern siehe unten.'],
                ['Rettungsdienst einweisen', 'Am Sammelplatz abholen und zur Einsatzstelle führen.'],
            ].map(([kopf, text], i) => (
                <li key={i} className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-slate-900 text-white font-black flex items-center justify-center text-sm">
                        {i + 1}
                    </span>
                    <span>
                        <b className="block text-base leading-tight text-slate-900">{kopf}</b>
                        <span className="text-sm text-slate-600">{text}</span>
                    </span>
                </li>
            ))}
        </ol>

        <div className="grid grid-cols-2 gap-4">
            <div className="border-2 border-red-600 rounded-lg p-3">
                <p className="text-[11px] font-black uppercase tracking-wider text-red-600 mb-1.5">Notruf</p>
                <table className="w-full text-sm">
                    <tbody>
                        {notrufnummern.map((n, i) => (
                            <tr key={i}>
                                <td className="py-0.5 pr-2 text-slate-700 align-top">
                                    {n.was}
                                    {n.zusatz && (
                                        <span className="block text-[10px] text-slate-500 leading-tight">
                                            {n.zusatz}
                                        </span>
                                    )}
                                </td>
                                <td className="py-0.5 whitespace-nowrap align-top text-right">
                                    {n.nummer
                                        ? <span className="font-black text-slate-900">{n.nummer}</span>
                                        : (
                                            <span className="font-bold text-amber-700 text-xs">
                                                nicht hinterlegt
                                            </span>
                                        )}
                                    {n.nummer && (
                                        <span className="block text-[10px] leading-tight font-bold text-slate-500">
                                            {n.geprueft === undefined
                                                ? ''
                                                : n.geprueft ? 'bestätigt' : 'nur gefunden'}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div>
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
                    Gefahrstoffe auf dieser Baustelle
                </p>
                <ul className="text-sm text-slate-800 font-semibold space-y-0.5 mb-3">
                    {stoffe.length > 0
                        ? stoffe.map((s, i) => <li key={i}>• {s}</li>)
                        : <li className="text-slate-400 font-normal">(noch nicht erfasst)</li>}
                </ul>
                <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">Sammelplatz</p>
                {sammelplatz
                    ? <p className="text-sm text-slate-800">{sammelplatz}</p>
                    : (
                        <p className="text-sm font-bold text-amber-700">
                            im Plan nicht festgelegt
                        </p>
                    )}
            </div>
        </div>

        {luecken.length > 0 && (
            <div className="mt-4 border-2 border-amber-500 rounded-lg p-2.5">
                <p className="text-sm font-bold text-amber-900 mb-0.5">
                    Dieses Blatt ist noch nicht vollständig
                </p>
                <ul className="text-xs text-amber-900 leading-tight">
                    {luecken.map((l, i) => <li key={i}>· {l}</li>)}
                </ul>
            </div>
        )}
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 2) WAS TRAGE ICH HIER — je Arbeitsbereich ein Blatt
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der praktisch wichtigste Aushang.
 *
 * Er beantwortet die einzige Frage, die jemand am Zugang zum Bereich hat, und
 * er beantwortet sie fuer GENAU DIESEN Bereich. Ein Blatt, auf dem alle drei
 * Schutzklassen nebeneinander stehen, verlangt vom Leser eine Zuordnung, die
 * er nicht treffen kann — und er nimmt dann die bequemste.
 */
export const AushangSchutzklasse: React.FC<{
    projektName: string;
    bereich: string;
    bereichName: string;
    felder: string[];
    klasse: Schutzklasse;
}> = ({ projektName, bereich, bereichName, felder, klasse }) => {
    const codes = klasse.psa.map(p => p.iso).filter((x): x is string => !!x);
    return (
        <Aushangblatt
            titel={`Bereich ${bereich}: ${klasse.key}`}
            untertitel={`${bereichName || 'Arbeitsbereich'}${felder.length ? ` · Felder ${felder.join(', ')}` : ''} · ${projektName}`}
            fuss="Diese Ausrüstung ist vollständig zu tragen. Eigenmächtige Reduzierung ist unzulässig."
        >
            <p className="text-lg font-bold text-slate-800 mb-3">{klasse.bezeichnung}</p>

            {codes.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b border-slate-200">
                    {codes.map((c, i) => <Zeichen key={i} code={c} gross />)}
                </div>
            )}

            <div className="grid grid-cols-2 gap-5">
                <div>
                    <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
                        Mindestausrüstung
                    </p>
                    <ul className="text-sm text-slate-800 space-y-1">
                        {klasse.psa.map((p, i) => <li key={i}>• {p.text}</li>)}
                    </ul>

                    {klasse.atemschutz.geraet && (
                        <>
                            <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mt-3 mb-1">
                                Atemschutz
                            </p>
                            <p className="text-sm text-slate-800">
                                {klasse.atemschutz.geraet}
                                {klasse.atemschutz.filtertyp && <> · Filter {klasse.atemschutz.filtertyp}</>}
                            </p>
                            {klasse.tragezeit?.ergebnis_min && (
                                <p className="text-sm font-bold text-slate-900 mt-1">
                                    Höchstens {klasse.tragezeit.ergebnis_min} Minuten am Stück,
                                    danach {klasse.tragezeit.erholung_min} Minuten Erholung.
                                </p>
                            )}
                            {klasse.atemschutz.wechsel_kriterien && klasse.atemschutz.wechsel_kriterien.length > 0 && (
                                <p className="text-xs text-slate-600 mt-1">
                                    Filter wechseln bei: {klasse.atemschutz.wechsel_kriterien.join(' · ')}
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div>
                    <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
                        Zusätzlich gilt
                    </p>
                    <ul className="text-sm text-slate-800 space-y-1">
                        {klasse.zusatz.map((z, i) => <li key={i}>• {z.text}</li>)}
                    </ul>

                    <div className="mt-4 border-2 border-amber-500 rounded-lg p-2.5">
                        <p className="text-sm font-bold text-amber-900 mb-0.5">Sofort melden</p>
                        <p className="text-xs text-amber-900">
                            Geruch, Verfärbung, unbekanntes Material oder ungewöhnlicher Boden:
                            Arbeit unterbrechen, Bereich verlassen, Aufsicht verständigen.
                            Die Schutzklasse wird dann angehoben.
                        </p>
                    </div>
                </div>
            </div>
        </Aushangblatt>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// 3) SCHLEUSENORDNUNG
// ═══════════════════════════════════════════════════════════════════════════

export const AushangSchleuse: React.FC<{
    projektName: string;
    parameter: SchleusenParameter;
}> = ({ projektName, parameter }) => (
    <Aushangblatt
        titel="Schwarz-Weiß-Anlage"
        untertitel={`Ablauf beim Verlassen des Bereichs · ${projektName}`}
        hoch={false}
        fuss="Die Anlage dient dem Kleidungswechsel und der Körperreinigung, nicht als Pausenraum und nicht zur Aufbewahrung von Speisen oder Freizeitkleidung."
    >
        <div className="grid grid-cols-2 gap-5 h-full">
            <div className="min-w-0">
                <SkizzeSvg zeichnung={zeichneSchleuse(parameter)} mitRahmen={false} />
            </div>
            <ol className="space-y-2 text-sm">
                {[
                    'Grobe Verschmutzung im Bereich abstreifen, Schuhwerk im Wasserbad reinigen.',
                    'Kammer 1: Einweganzug, Maske und Handschuhe ablegen und in den bereitstehenden Behälter geben. Keine Mehrfachverwendung.',
                    'Kammer 2: Hände und Gesicht waschen, Mund ausspülen.',
                    'Kammer 3: saubere Kleidung anlegen.',
                    'Nach Schichtende: duschen mit gründlicher Körperreinigung.',
                    'Eintrag im Schleusenbuch nicht vergessen.',
                ].map((t, i) => (
                    <li key={i} className="flex gap-2.5">
                        <span className="w-6 h-6 shrink-0 rounded-full bg-slate-900 text-white font-black flex items-center justify-center text-xs">
                            {i + 1}
                        </span>
                        <span className="text-slate-800">{t}</span>
                    </li>
                ))}
                <li className="flex gap-2.5 pt-2">
                    <span className="w-6 h-6 shrink-0 rounded-full bg-red-600 text-white font-black flex items-center justify-center text-xs">
                        !
                    </span>
                    <span className="text-slate-900 font-bold">
                        Gereinigt wird immer von Weiß nach Schwarz, nie umgekehrt.
                    </span>
                </li>
            </ol>
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 4) BAUSTELLENHYGIENE
// ═══════════════════════════════════════════════════════════════════════════

export const AushangHygiene: React.FC<{ projektName: string }> = ({ projektName }) => (
    <Aushangblatt
        titel="Verhaltensregeln im Schwarzbereich"
        untertitel={projektName}
        fuss="Die Regeln schützen nicht nur vor Aufnahme über den Mund, sondern auch davor, Schadstoffe nach Hause zu tragen."
    >
        <div className="flex gap-3 mb-4">
            <Zeichen code="P006" text="Zutritt nur mit Einweisung und Schutzausrüstung" gross />
            <Zeichen code="M014" gross />
            <Zeichen code="M008" gross />
            <Zeichen code="M009" gross />
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {[
                'Rauchen, Essen und Trinken sind im Schwarzbereich verboten.',
                'Zigaretten, Mobiltelefone und Taschen bleiben draußen.',
                'Pausen ausschließlich außerhalb der abgesperrten Bereiche.',
                'Einwegschutzkleidung und Masken bei jeder Unterbrechung entsorgen.',
                'Beim Verlassen: PSA ablegen, Hände und Gesicht waschen.',
                'Schuhwerk im Wasserbad reinigen oder wechseln.',
                'Auf der Baustelleneinrichtung erneut waschen, Mund ausspülen.',
                'Erst danach Pausenraum betreten, essen, trinken, rauchen.',
                'Nach jedem Arbeitstag duschen.',
                'Schutzhelm einmal je Schicht feucht reinigen oder doppelt vorhalten.',
                'Arbeitsschuhe, Mehrweghandschuhe und Geräte vor dem Ablegen reinigen.',
                'Baumaschinen vor dem Verlassen absaugen und Räder benetzen.',
            ].map((t, i) => (
                <p key={i} className="flex gap-2 text-slate-800">
                    <span className="font-black text-slate-400">·</span> {t}
                </p>
            ))}
        </div>

        <div className="mt-4 border-2 border-slate-800 rounded-lg p-3">
            <p className="font-bold text-slate-900 mb-0.5">Haftet Material an der Schutzkleidung?</p>
            <p className="text-sm text-slate-700">
                Dann wird sie unverzüglich gewechselt, nicht abgebürstet und nicht ausgeklopft.
                Das gilt auch für Handschuhe und Schuhe.
            </p>
        </div>
    </Aushangblatt>
);

// ═══════════════════════════════════════════════════════════════════════════
// 5) NOTFALLAUSWEIS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der Notfallausweis nach Anhang 2 der DGUV Regel 101-004.
 *
 * Beide Seiten nebeneinander auf einem Blatt: so kann man ihn ausdrucken,
 * falten und laminieren. Der personenbezogene Teil bleibt LEER — er wird von
 * Hand ausgefuellt. Vorgedruckte Namen auf einem Stapel Ausweise fuehren
 * dazu, dass jemand den falschen einsteckt.
 */
export const AushangNotfallausweis: React.FC<{
    projektName: string;
    projektNummer?: string;
    stoffe: string[];
    giftnotruf: string;
    koordinator: string;
    /**
     * Die Notfallstellen aus dem Notrufplan des SiGe-Plans — gelesen, nicht
     * kopiert. Auf dem Ausweis stehen nur die beiden, die im Ernstfall
     * angefahren werden: Klinik und Durchgangsarzt. Ein Ausweis in Scheck-
     * kartengroesse vertraegt keine Liste, und die Apotheke rettet niemanden.
     */
    stellen?: NotfallStelle[];
}> = ({ projektName, projektNummer, stoffe, giftnotruf, koordinator, stellen = [] }) => (
    <Aushangblatt
        titel="Notfallausweis"
        untertitel="Ausschneiden, falten, laminieren. Von jeder Person mitzuführen, auch außerhalb der Arbeitszeit"
        hoch={false}
        fuss="DGUV Regel 101-004 Abschnitt 14 i. V. m. Anhang 2 · Eine Kopie verbleibt beim Auftragnehmer und beim Koordinator."
    >
        <div className="grid grid-cols-2 gap-4 h-full">
            <div className="border-2 border-slate-800 rounded-lg p-3">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Vorderseite</p>
                <p className="font-black text-slate-900 leading-tight mb-2">
                    NOTFALLAUSWEIS<br />
                    <span className="text-xs font-bold">für Arbeiten in kontaminierten Bereichen</span>
                </p>
                <p className="text-xs text-slate-700 mb-3">
                    {projektName}{projektNummer && <> · {projektNummer}</>}
                </p>
                {['Name, Vorname', 'Geburtsdatum', 'Wohnort / Straße', 'Arbeitgeber', 'Ansprechpartner / Telefon', 'Hausarzt']
                    .map((f, i) => (
                        <p key={i} className="text-[11px] text-slate-600 mb-2">
                            {f}: <span className="inline-block border-b border-slate-400 w-full" />
                        </p>
                    ))}
            </div>

            <div className="border-2 border-slate-800 rounded-lg p-3">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Rückseite</p>
                <p className="text-[11px] font-black text-slate-900 mb-1">GEFAHRSTOFFE AUF DER BAUSTELLE</p>
                <ul className="text-[11px] text-slate-800 mb-3">
                    {stoffe.length > 0
                        ? stoffe.map((s, i) => <li key={i}>• {s}</li>)
                        : <li className="text-slate-400">(im Plan erfassen)</li>}
                </ul>

                <p className="text-[11px] font-black text-slate-900 mb-1">ARBEITSMEDIZINISCHE VORSORGE</p>
                <p className="text-[11px] text-slate-600 mb-3">
                    Letzte Untersuchung am: <span className="inline-block border-b border-slate-400 w-24" />
                    {' '}durch: <span className="inline-block border-b border-slate-400 w-24" />
                </p>

                <div className="border-t border-slate-300 pt-2">
                    <p className="text-[11px] font-black text-slate-900">RETTUNGSDIENST 112</p>
                    {stellen
                        .filter(x => x.key === 'krankenhaus' || x.key === 'durchgangsarzt')
                        .map(x => (
                            <p key={x.key} className="text-[11px] text-slate-800">
                                {x.was} {x.name}
                                {x.telefon && <> · {x.telefon}</>}
                                {x.entfernung && <> · {x.entfernung}</>}
                                {!x.geprueft && (
                                    <span className="text-slate-500"> (nur gefunden)</span>
                                )}
                            </p>
                        ))}
                    <p className="text-[11px] text-slate-800">
                        Giftnotruf{' '}
                        {giftnotruf || <span className="font-bold text-amber-700">nicht hinterlegt</span>}
                    </p>
                    <p className="text-[11px] text-slate-800">
                        Koordinator{' '}
                        {koordinator || <span className="font-bold text-amber-700">nicht hinterlegt</span>}
                    </p>
                    <p className="text-[10px] text-slate-600 mt-1.5">
                        Bei Verdacht auf Vergiftung: Giftnotrufzentrale hinzuziehen und die oben
                        genannten Stoffe nennen.
                    </p>
                </div>
            </div>
        </div>
    </Aushangblatt>
);
